import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root'
})
export class NotificationUtils {
    private static instance: NotificationUtils;
    private snackBar: MatSnackBar;
    private activeSnackbars: MatSnackBarRef<any>[] = [];
    private snackbarQueue: {message: string, action: string, config: MatSnackBarConfig}[] = [];
    private isProcessingQueue = false;

    constructor(snackBar: MatSnackBar) {
        this.snackBar = snackBar;
        NotificationUtils.instance = this;
    }

    static getInstance(): NotificationUtils {
        return NotificationUtils.instance;
    }

    private processSnackbarQueue(): void {
        if (this.isProcessingQueue || this.snackbarQueue.length === 0) {
            return;
        }

        this.isProcessingQueue = true;
        const nextSnackbar = this.snackbarQueue.shift();
        
        if (nextSnackbar) {
            const snackBarRef = this.snackBar.open(
                nextSnackbar.message, 
                nextSnackbar.action, 
                nextSnackbar.config
            );
            
            this.activeSnackbars.push(snackBarRef);
            
            snackBarRef.afterDismissed().subscribe(() => {
                // Remove this snackbar from active list
                const index = this.activeSnackbars.indexOf(snackBarRef);
                if (index > -1) {
                    this.activeSnackbars.splice(index, 1);
                }
                
                // Process next in queue after a small delay
                setTimeout(() => {
                    this.isProcessingQueue = false;
                    this.processSnackbarQueue();
                }, 300);
            });
        } else {
            this.isProcessingQueue = false;
        }
    }

    private showNotification(message: string, action: string, config: MatSnackBarConfig): void {
        // Add to queue
        this.snackbarQueue.push({message, action, config});
        
        // Process queue
        this.processSnackbarQueue();
    }

    showSuccess(message: string): void {
        const config: MatSnackBarConfig = {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['success-snackbar']
        };
        
        this.showNotification(message, 'Close', config);
    }

    showError(message: string): void {
        const config: MatSnackBarConfig = {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar']
        };
        
        this.showNotification(message, 'Close', config);
    }

    /**
     * Formats an entity name from snake_case to Title Case
     * Example: "bank_transaction" becomes "Bank Transaction"
     */
    formatEntityName(entityName: string): string {
        if (!entityName) return '';
        
        return entityName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    showActionResult(success: boolean, entityName: string, action: 'created' | 'updated' | 'deleted'): void {
        const formattedName = this.formatEntityName(entityName);
        
        if (success) {
            this.showSuccess(`${formattedName} ${action} successfully`);
        } else {
            this.showError(`Error ${action === 'deleted' ? 'deleting' : action === 'updated' ? 'updating' : 'creating'} ${formattedName}`);
        }
    }
}
